import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Trash2, RefreshCw, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const GroupRow = ({ group, api, selected, onSaved }) => {
  const [name, setName] = useState(group.name);
  const save = async (patch, msg) => {
    try {
      await api().put(`/admin/assignment-groups/${group.id}`, patch);
      toast.success(msg);
      onSaved();
    } catch {
      toast.error('Failed to update group');
    }
  };
  const remove = async () => {
    if (!window.confirm(`Delete group "${group.name}"?`)) return;
    try {
      await api().delete(`/admin/assignment-groups/${group.id}`);
      toast.success('Group deleted');
      onSaved();
    } catch {
      toast.error('Failed to delete group');
    }
  };
  return (
    <div className="p-3 rounded-lg border border-border space-y-2" data-testid={`group-row-${group.id}`}>
      <div className="flex gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 bg-black border-border text-white" data-testid={`group-name-input-${group.id}`} />
        <Button size="sm" variant="outline" disabled={!name.trim() || name === group.name} onClick={() => save({ name }, 'Group renamed')} data-testid={`group-rename-btn-${group.id}`}>
          <Check className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="outline" className="text-red-400 border-red-500/30" onClick={remove} data-testid={`group-delete-btn-${group.id}`}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{group.member_ids.length} member{group.member_ids.length === 1 ? '' : 's'}</span>
        <button type="button" disabled={!selected.length} onClick={() => save({ member_ids: selected }, `Members updated (${selected.length})`)} className="inline-flex items-center gap-1 text-primary disabled:opacity-40" data-testid={`group-update-members-btn-${group.id}`}>
          <RefreshCw className="h-3 w-3" /> Replace members with current selection ({selected.length})
        </button>
      </div>
    </div>
  );
};

export const GroupManagerDialog = ({ mode, onClose, api, groups, selected, onSaved }) => {
  const [name, setName] = useState('');
  useEffect(() => { if (mode === 'create') setName(''); }, [mode]);

  const create = async () => {
    try {
      await api().post('/admin/assignment-groups', { name, member_ids: selected });
      toast.success(`Group "${name}" saved`);
      onSaved();
      onClose();
    } catch {
      toast.error('Failed to save group');
    }
  };

  return (
    <Dialog open={!!mode} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border max-w-md max-h-[85vh] overflow-y-auto" data-testid="group-manager-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-wider text-white">
            {mode === 'create' ? 'Save Selection as Group' : 'Manage Groups'}
          </DialogTitle>
        </DialogHeader>
        {mode === 'create' ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{selected.length} selected member{selected.length === 1 ? '' : 's'} will be saved.</p>
            <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder='e.g. "Morning Bootcamp"' maxLength={60} className="bg-black border-border text-white" data-testid="new-group-name-input" />
            <Button onClick={create} disabled={!name.trim()} className="w-full bg-primary text-black hover:bg-primary/90" data-testid="new-group-save-btn">Save Group</Button>
          </div>
        ) : (
          <div className="space-y-3">
            {groups.map((g) => <GroupRow key={`${g.id}-${g.name}`} group={g} api={api} selected={selected} onSaved={onSaved} />)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GroupManagerDialog;
